'use client';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import { PROMO_BAR_HEIGHT } from './RotatingPromoBar/styles';

export const NavLink = styled(Link)`
  color: inherit;
  background-color: transparent;
  outline: none;
  border: none;
  margin-bottom: 2px !important;
  display: inline;

  &:hover {
    padding-bottom: 0;
    color: ${props => props.theme.colors.secondary.lilac};
  }

  @media ${props => props.theme.tablet} {
    ${props => props.theme.typography.body};
    color: inherit;
    &:last-child {
      margin-right: 0;
    }

    &:first-child {
      margin-left: 0;
    }
  }
`;

export const SmallNavLink = styled(NavLink)`
  display: ${props => (props.showLink ? 'inline' : 'none')};
  ${props => props.theme.typography.body};
  &:focus {
    outline: 0;
  }
  @media ${props => props.theme.mobileL} {
    display: inline;
  }
`;

export const Header = styled.header`
  display: block;
  background-color: ${props =>
    props.$fullBleedNav && !props.$scrolled
      ? 'transparent'
      : props.theme.colors.get(props.$bgColor)};
  color: ${props => props.theme.colors.get(props.$fontColor)};
`;

export const NavWrapper = styled.div`
  z-index: 100;
  background-color: ${props =>
    props.$fullBleedNav && !props.$scrolled
      ? 'transparent'
      : props.theme.colors.get(props.$bgColor)};
  color: ${props => props.theme.colors.get(props.$fontColor)};
  ${props =>
    props.$sticky &&
    css`
      position: fixed;
      top: ${props.$navTopOffset ?? 0}px;
      left: 0;
      right: 0;
      width: 100%;
      transition: background-color 250ms ease-in-out;
    `};
`;

export const StyledLoginButton = styled.button`
  display: ${props => (props.showButton ? 'inline' : 'none')};
  ${props => props.theme.typography.body};
  outline: none;
  border: none;
  color: inherit;
  padding: 14px 2px 2px 2px;
  &:focus,
  &:hover {
    text-decoration: none;
    padding-bottom: 0;
    border-bottom: 2px solid ${props => props.theme.colors.border.dark};
  }
  background: transparent;

  @media ${props => props.theme.laptop} {
    display: inline;
    margin: 0 12px 2px;
  }
`;

export const StyledHeaderButton = styled.button`
  background: transparent;
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
  display: inline;
  outline: none;
  color: inherit;
  border: none;
  padding: 0;
  &:focus,
  &:hover {
    text-decoration: none;
    padding-bottom: 0;
    border-bottom: 2px solid ${props => props.theme.colors.secondary.lilac};
  }
  margin-bottom: 2px !important;
  @media ${props => props.theme.tablet} {
    ${props => props.theme.typography.body};
  }
`;

// this handles the Profile Icon color change
export const StyledProfileWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  svg {
    vertical-align: bottom;
  }
  &:hover {
    svg {
      fill: none;
      stroke: ${({ theme }) => theme.colors.secondary.lilac};
    }

    ${SmallNavLink} {
      color: ${({ theme }) => theme.colors.secondary.lilac};
      text-decoration: underline;
      text-underline-offset: ${props => props.theme.spacing(2)};
    }
  }
`;

export const DesktopButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(3)};
`;

export const logoImgContainerHeight = '48px';

export const LogoImgContainer = styled.div`
  max-height: 48px;
  width: auto;

  & img {
    max-height: 100%;
    width: auto;
    min-height: 48px;
  }
  height: ${logoImgContainerHeight};
  z-index: 10;
`;

// Estimated to match NavigationBar's own padding + logo height (48px),
// so this has a correct height on SSR/first paint before the
// ResizeObserver-based measurement in BaseHeader corrects it — otherwise
// page content briefly renders underneath the fixed nav on every load.
export const NavSpacer = styled.div`
  height: calc(
    ${props => props.theme.spacing(props.$verticalPadding ?? 6)} * 2 +
      ${logoImgContainerHeight}
  );
`;

// RotatingPromoBar is position:fixed whenever it's sticky (see its own
// styles), so its flow height must be reserved separately here.
export const BannerSpacer = styled.div`
  height: ${PROMO_BAR_HEIGHT}px;
`;

export const LogoLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px solid transparent;
  &:focus,
  &:active {
    border: 2px solid ${props => props.theme.colors.border.dark};
    border-radius: 16px;
  }
`;

export const NavigationMenuLink = styled.li`
  list-style: none;
  & button {
    margin: 0;
  }

  ${props =>
    props.$variant === 'group'
      ? css`
          color: ${props.$fontColor
            ? props.theme.colors.get(props.$fontColor)
            : props.theme.colors.text.lightest};
        `
      : css`
          ${props.theme.typography.heading3};
          display: flex;
          text-align: center;
          justify-content: center;
          margin-bottom: ${props.theme.spacing(2)};
        `}
`;
